import type { Booking, BookingAccessory, DepositRefundPoint } from '../api/bookings';
import { depositSplit } from './deposit';

/**
 * Settling the deposit at return.
 *
 * <p>
 * <strong>Shown as subtraction, line by line, because this is the highest-dispute moment in the
 * business.</strong> Handing a customer a single net number while they are standing at the counter
 * with a garment they believe is undamaged is how a shop loses the argument. Every deduction is
 * named, attributed to a wearer where it belongs to one, and priced from a rule rather than
 * improvised.
 * </p>
 *
 * <p>
 * The other direction is on the booking copy, where the deposit is <em>added</em> to what the
 * customer pays. Two documents, two directions, one number.
 * </p>
 */

export interface SettlementDeduction {
  /** What it is, in the customer's words. */
  label: string;
  amount: number;
  /** Who it belongs to, where a person owns it. Damage does; a late fee is the booking's. */
  wearerName?: string;
}

export interface Settlement {
  depositHeld: number;
  deductions: SettlementDeduction[];
  deductionTotal: number;
  /**
   * What is left of the deposit paying what the customer still owed before the return — the rent
   * not yet paid, an extension, an alteration (owner, 2026-10-14: the pending payment comes off the
   * deposit). The server does the same arithmetic when the counter sends `applyToBalance`, which the
   * commands always do. Charges are typed including GST, so no tax is hidden in any of this.
   */
  towardsBalance: number;
  /** Paid back to the customer. Never negative — see {@link shortfall}. */
  refund: number;
  /**
   * Owed beyond the deposit, and therefore unsecured.
   *
   * <p>
   * This is the number that makes the "refund at the counter" setting a real decision. Deductions
   * larger than the deposit leave the shop chasing a customer who has already walked out, and a
   * shop that refunds before cleaning has inspected the garments will meet this more often. A
   * settlement screen that clamped at zero and said nothing would hide it.
   * </p>
   */
  shortfall: number;
  /**
   * Everything the customer still has to hand over once the deposit has done all it can: the
   * {@link shortfall}, plus whatever of the old balance the deposit did not reach. What the settle
   * button takes in the same press.
   */
  toCollect: number;
}

/**
 * What the shop keeps and what it gives back.
 *
 * `lateFee` is passed in rather than derived here: the grace period and the daily rate are tenant
 * settings, and a screen inventing them would disagree with whatever the backend charges.
 */
export function settleBooking(
  booking: Booking,
  lateFee = 0,
  comingBack?: ReadonlySet<number>,
): Settlement {
  const { held } = depositSplit(booking.deposit);

  // Held is what the shop physically has. A deposit never taken cannot fund a deduction, and the
  // whole charge becomes a shortfall rather than silently reducing a refund that does not exist.
  const depositHeld = held > 0 ? held : 0;

  const deductions: SettlementDeduction[] = [];

  for (const wearer of booking.wearers) {
    for (const garment of wearer.garments) {
      if (garment.damageCharge && garment.damageCharge > 0) {
        deductions.push({
          label: garment.conditionIn ?? `Damage · ${garment.itemName}`,
          amount: garment.damageCharge,
          wearerName: wearer.name,
        });
      }

      for (const accessory of garment.accessories) {
        // At the return counter this is the clerk's live ticks, so the arithmetic moves as they
        // untick a missing kalgi; everywhere else it is what the server has recorded. Reading only
        // the stored value would leave the counter showing a settlement that ignores the very
        // decision being made on it.
        const back =
          comingBack === undefined ? accessory.returnedOn !== null : comingBack.has(accessory.id);

        // Only what actually went out can go missing, and only a priced one is chargeable.
        if (accessory.issued && !back && accessory.replacementCharge) {
          deductions.push({
            label: `${accessory.name} not returned`,
            amount: accessoryReplacementTotal(accessory),
            wearerName: wearer.name,
          });
        }
      }
    }
  }

  if (lateFee > 0) {
    deductions.push({ label: 'Late return fee', amount: lateFee });
  }

  const deductionTotal = deductions.reduce((sum, deduction) => sum + deduction.amount, 0);

  // What was owed before this settlement — the server's figure, never re-derived. The charges above
  // are not in it yet; they are billed by the settlement itself.
  const owed = Math.max(0, booking.balanceDue);
  const left = Math.max(0, depositHeld - deductionTotal);
  const towardsBalance = Math.min(left, owed);
  const shortfall = Math.max(0, deductionTotal - depositHeld);

  return {
    depositHeld,
    deductions,
    deductionTotal,
    towardsBalance,
    refund: left - towardsBalance,
    shortfall,
    toCollect: shortfall + (owed - towardsBalance),
  };
}

/**
 * The portion of the deposit the shop keeps, which becomes taxable.
 *
 * A deposit is not a supply while it is merely held — but money forfeited is consideration for
 * damage, and §25 taxes it. The refunded part never was.
 */
export function forfeitedAmount(settlement: Settlement): number {
  return Math.min(settlement.depositHeld, settlement.deductionTotal);
}

/** Every accessory this booking's issue counter recorded as having left the shop. */
export function accessoriesIssued(booking: Booking): BookingAccessory[] {
  return booking.wearers
    .flatMap((wearer) => wearer.garments)
    .flatMap((garment) => garment.accessories)
    .filter((accessory) => accessory.issued);
}

/**
 * The accessory ids to report as returned, given the garments coming back and what is still ticked.
 *
 * <p>
 * Narrowed to the garments actually being received: the server refuses an id that did not go out on
 * this booking, and a partial return must not book in the accessories of garments still with the
 * customer.
 * </p>
 */
export function accessoriesBack(
  booking: Booking,
  received: ReadonlySet<number>,
  ticked: ReadonlySet<number>,
): number[] {
  return booking.wearers
    .flatMap((wearer) => wearer.garments)
    .filter((garment) => received.has(garment.id))
    .flatMap((garment) => garment.accessories)
    .filter((accessory) => accessory.issued && ticked.has(accessory.id))
    .map((accessory) => accessory.id);
}

/**
 * What an accessory costs the customer if it does not come back.
 *
 * <p>
 * <strong>Exported because it was quietly living in two places at different values.</strong> The
 * settlement multiplied by the quantity and the customer copy's terms did not, so a booking with a
 * pair of cufflinks promised one figure on paper and deducted twice it at the counter. One
 * definition, used by both.
 * </p>
 *
 * <p>
 * An unpriced accessory is zero rather than absent: the shop absorbs the loss, which is a decision
 * it made rather than a charge nobody set. A negative quantity is floored — a row nobody could
 * explain must not credit the customer.
 * </p>
 */
export function accessoryReplacementTotal(accessory: BookingAccessory): number {
  return (accessory.replacementCharge ?? 0) * Math.max(0, accessory.quantity);
}

/**
 * Whether a late fee typed at the return counter is actually taken by this call.
 *
 * <p>
 * <strong>The server's rule, mirrored — and it exists because the client was offering a write the
 * server discarded.</strong> The fee is only ever an input to settlement: it funds a deduction
 * against the deposit and is stored nowhere else, so on a partial return, or where the shop refunds
 * after inspection or after cleaning, it had nowhere to go. The counter showed it coming off the
 * deposit, the settle succeeded, and the figure existed nowhere afterwards.
 * </p>
 *
 * <p>
 * Both conditions are the server's own: the deposit is settled by a return only once every garment
 * is back — which is what makes the stage `Completed` — and only where the refund point says now.
 * `POST /deposit/settle` takes the same figure and is where a deferred shop charges it.
 * </p>
 */
export function lateFeeIsTakenNow(options: {
  /** True while some garment that went out is still out, so the booking is not yet complete. */
  partial: boolean;
  refundPoint: DepositRefundPoint;
}): boolean {
  return !options.partial && options.refundPoint === 'AtCounter';
}

/**
 * What the take-back button says it will do — and only what the call will actually do.
 *
 * <p>
 * The deposit is settled by this call only when everything is back <em>and</em> the refund point is
 * the counter (the server's rule, the same one {@link lateFeeIsTakenNow} reads). Anywhere else the
 * garments come back and the deposit stays open, so the button names garments, not money. It said
 * "Settle & refund ₹4,000" on a shop refunding after inspection, where the call refunded nothing and
 * the customer had just been told otherwise.
 * </p>
 */
export function takeBackLabel(
  options: {
    received: number;
    outstanding: number;
    refundPoint: DepositRefundPoint;
    refund: number;
    /** What the deposit does not cover and the same press takes — `Settlement.toCollect`. */
    toCollect?: number;
  },
  money: (amount: number) => string,
): string {
  const settling = lateFeeIsTakenNow({
    partial: options.received < options.outstanding,
    refundPoint: options.refundPoint,
  });

  if (!settling) {
    return `Take back ${options.received} garment${options.received === 1 ? '' : 's'}`;
  }

  if ((options.toCollect ?? 0) > 0) {
    return `Take back & take ${money(options.toCollect ?? 0)}`;
  }

  return options.refund > 0 ? `Take back & refund ${money(options.refund)}` : 'Take back & settle';
}

/** The settle button: what goes back, or what is taken, or neither — "Refund ₹3,000 by UPI". */
export function settleLabel(
  settlement: Pick<Settlement, 'refund' | 'toCollect'>,
  methodName: string,
  money: (amount: number) => string,
): string {
  if (settlement.toCollect > 0) {
    return `Settle and take ${money(settlement.toCollect)} by ${methodName}`;
  }

  return settlement.refund > 0
    ? `Refund ${money(settlement.refund)} by ${methodName} and close`
    : 'Settle the deposit';
}
